import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';

const InvoiceView = () => {
  const { division, invoiceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('');
  const [companyDetails, setCompanyDetails] = useState({});
  const [clientDetails, setClientDetails] = useState({});
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    const passedCompany = location.state?.company;
    const storedCompany = localStorage.getItem('selectedCompany');
    const resolvedCompany = passedCompany || storedCompany;

    if (!resolvedCompany) {
      alert('Company not found. Please login again.');
      navigate('/admin/dashboard');
      return;
    }

    setCompany(resolvedCompany.trim());
  }, [location.state, navigate]);

  useEffect(() => {
    if (!company || !division || !invoiceId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const invoicePath = `${company}/${division}/billSummary/${invoiceId}`;
        const companyPath = `companyDetails/${company}`;
        const clientPath = `${company}/${division}/info`;
        const entryPassPath = `${company}/${division}/entryPass`;

        const [invoiceSnap, companySnap, clientSnap, entrySnap] = await Promise.all([
          get(ref(realtimeDb, invoicePath)),
          get(ref(realtimeDb, companyPath)),
          get(ref(realtimeDb, clientPath)),
          get(ref(realtimeDb, entryPassPath))
        ]);

        if (invoiceSnap.exists()) {
          setInvoice(invoiceSnap.val());
        }

        if (companySnap.exists()) {
          setCompanyDetails(companySnap.val());
        }

        if (clientSnap.exists()) {
          setClientDetails(clientSnap.val());
        }

        if (entrySnap.exists()) {
          const entryData = entrySnap.val();
          const filteredEntries = Object.values(entryData).filter(
            entry => entry.workBillNo === invoiceSnap.val()?.billNumber
          );
          setEntries(filteredEntries);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Failed to load invoice data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company, division, invoiceId]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(`/admin/division/${division}`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ width: 40, height: 40, border: '4px solid #3b82f6', borderTop: '4px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 16 }}>Invoice not found</h2>
          <button
            onClick={handleBack}
            style={{ background: '#2563eb', color: 'white', padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <FaArrowLeft /> Back to Division
          </button>
        </div>
      </div>
    );
  }

  const numberToWords = (num) => {
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
      'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
      'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    const convertChunk = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + convertChunk(n % 100) : '');
      return '';
    };

    if (num === 0) return 'zero';

    let result = '';
    const crore = Math.floor(num / 10000000);
    if (crore) result += convertChunk(crore) + ' crore ';
    num %= 10000000;

    const lakh = Math.floor(num / 100000);
    if (lakh) result += convertChunk(lakh) + ' lakh ';
    num %= 100000;

    const thousand = Math.floor(num / 1000);
    if (thousand) result += convertChunk(thousand) + ' thousand ';
    num %= 1000;

    if (num) result += convertChunk(num);

    return result.trim();
  };

  const billSummary = {
    grossTotal: invoice.grossTotal || 0,
    cgst: invoice.cgst || 0,
    sgst: invoice.sgst || 0,
    finalTotal: invoice.finalTotal || 0
  };

  const paddedEntries = [...entries];
  while (paddedEntries.length < 5) {
    paddedEntries.push({
      id: `placeholder_${paddedEntries.length + 1}`,
      jobNo: '-', make: '-', makeSrNo: '-', kva: '-',
      type: '-', oilCap: '-', finalChallanNo: '-',
      finalVehicleNo: '-', moNumber: '-', materialType: '-'
    });
  }

  return (
    <div style={{ padding: 24, background: '#f3f4f6', minHeight: '100vh' }}>
      <style>{`
        @page { size: A4; margin: 15mm 10mm; }
        .invoice-container { width: 100%; max-width: 210mm; margin: 0 auto; padding: 10mm; box-sizing: border-box; }
        .inv-header { text-align: center; margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 10px; }
        .inv-company-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
        .inv-company-desc { font-size: 14px; margin-bottom: 5px; }
        .inv-company-address { font-size: 12px; }
        .inv-title { font-size: 18px; font-weight: bold; margin: 15px 0; text-decoration: underline; text-align: center; }
        .inv-bill-info { display: flex; flex-direction: column; font-size: 14px; margin-bottom: 10px; text-align: end; }
        .inv-address-box { display: flex; border: 1px solid #000; margin-bottom: 10px; }
        .inv-address-section { flex: 1; padding: 8px; font-size: 12px; }
        .inv-address-section:first-child { border-right: 1px solid #000; }
        .inv-work-details { font-size: 12px; margin-bottom: 10px; }
        .inv-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 10px; }
        .inv-table th, .inv-table td { border: 1px solid #000; padding: 6px; text-align: center; }
        .inv-table th { background-color: #f2f2f2; font-weight: bold; }
        .inv-amount-row { text-align: right; padding-right: 30px; font-size: 12px; margin: 3px 0; }
        .inv-total-amount { font-weight: bold; font-size: 13px; }
        .inv-amount-words { font-size: 12px; margin: 10px 0; padding: 5px; border-top: 1px dashed #000; border-bottom: 1px dashed #000; }
        .inv-signature { float: right; margin-top: 40px; text-align: center; font-weight: bold; font-size: 14px; }
        .inv-footer { margin-top: 60px; font-size: 10px; text-align: center; border-top: 1px solid #000; padding-top: 5px; }
        @media print { 
          .no-print { display: none !important; } 
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @media screen {
          .invoice-container { box-shadow: 0 0 10px rgba(0,0,0,0.1); background: white; }
        }
      `}</style>

      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <button
          onClick={handleBack}
          style={{ background: '#d1d5db', padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <FaArrowLeft /> Back to Division
        </button>
        <button
          onClick={handlePrint}
          style={{ background: '#2563eb', color: 'white', padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <FaPrint /> Print Invoice
        </button>
      </div>

      <div className="invoice-container">
        <div className="inv-header">
          <div className="inv-company-name">{company}</div>
          <div className="inv-company-desc">Transformer Service</div>
          <div className="inv-company-address">Address: {companyDetails.address || 'N/A'}</div>
        </div>

        <div className="inv-title">TAX INVOICE</div>

        <div className="inv-bill-info">
          <div>Bill No: {invoice.billNumber}</div>
          <div>Date: {invoice.billDate}</div>
        </div>

        <div className="inv-address-box">
          <div className="inv-address-section">
            <p><strong>Name:</strong> {company}</p>
            <p><strong>Address:</strong> {companyDetails.address || 'N/A'}</p>
            <p><strong>Email:</strong> {companyDetails.email || 'N/A'}</p>
          </div>
          <div className="inv-address-section">
            <p><strong>Name:</strong> The Executive</p>
            <p><strong>Address:</strong> {clientDetails.address || '__________'}</p>
            <p><strong>Email:</strong> {clientDetails.email || '__________'}</p>
            <p><strong>GST No:</strong> {clientDetails.gst || '__________'}</p>
          </div>
        </div>

        <div className="inv-work-details">
          <p><strong>Name of work:</strong> Repairing of DT_UPTO_100 KVA Distribution ({division})</p>
          <p><strong>Ref:</strong></p>
          <p>1. Tender Ref: {companyDetails.tender || 'N/A'}</p>
          <p>2. Letter of Empanelment.</p>
          <p>3. EE/DTRR/WO/{invoice.workOrderNo}/{invoice.workOrderDate}</p>
        </div>

        <table className="inv-table">
          <thead>
            <tr>
              <th style={{ width: '5%' }}>Sr No</th>
              <th style={{ width: '7%' }}>HSN Code</th>
              <th style={{ width: '10%' }}>Description</th>
              <th style={{ width: '8%' }}>Job No</th>
              <th style={{ width: '8%' }}>Make</th>
              <th style={{ width: '8%' }}>Make Sr No</th>
              <th style={{ width: '6%' }}>KVA</th>
              <th style={{ width: '6%' }}>Type</th>
              <th style={{ width: '6%' }}>Oil Cap</th>
              <th style={{ width: '8%' }}>Final Challan No</th>
              <th style={{ width: '8%' }}>Final Vehicle No</th>
              <th style={{ width: '8%' }}>MO Number</th>
              <th style={{ width: '8%' }}>Material</th>
              <th style={{ width: '8%' }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {paddedEntries.slice(0, 5).map((item, idx) => (
              <tr key={item.id}>
                <td>{idx + 1}</td>
                <td>---</td>
                <td>Repair of Transformer</td>
                <td>{item.jobNo}</td>
                <td>{item.make}</td>
                <td>{item.makeSrNo}</td>
                <td>{item.kva}</td>
                <td>{item.type}</td>
                <td>{item.oilCap}</td>
                <td>{item.finalChallanNo}</td>
                <td>{item.finalVehicleNo}</td>
                <td>{item.moNumber}</td>
                <td>{item.materialType || 'ALU'}</td>
                <td>{item.amount ? parseFloat(item.amount).toFixed(2) : '0.00'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="inv-amount-row">Gross Bill: ₹{billSummary.grossTotal.toFixed(2)}</div>
        <div className="inv-amount-row">Add CGST 9%: ₹{billSummary.cgst.toFixed(2)}</div>
        <div className="inv-amount-row">Add SGST 9%: ₹{billSummary.sgst.toFixed(2)}</div>
        <div className="inv-amount-row inv-total-amount">Total Amount: ₹{billSummary.finalTotal.toFixed(2)}</div>

        <div className="inv-amount-words">
          <p><strong>Total Amount (In Rupees):</strong> ₹{billSummary.finalTotal.toFixed(2)}</p>
          <p><strong>Total Amount (In Words):</strong> {numberToWords(Math.round(billSummary.finalTotal))} rupees only</p>
        </div>

        <div className="inv-signature">
          <p>{company}</p>
          <p>Proprietor</p>
        </div>

        <div className="inv-footer">
          <p>This is a computer generated invoice and does not require signature</p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;